import React from 'react';
import { Edit, Power, PowerOff, Beaker } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { IngredientCategory } from '../types/system-parameters.types';

interface IngredientCategoriesTableProps {
  categories: IngredientCategory[];
  onEdit: (category: IngredientCategory) => void;
  onDelete: (category: IngredientCategory) => void;
}

type SortField = 'name' | 'displayOrder';

export const IngredientCategoriesTable: React.FC<IngredientCategoriesTableProps> = ({
  categories,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    name: 'Categoría',
    displayOrder: 'Orden'
  };

  const sortValueExtractor = (category: IngredientCategory, field: SortField): any => {
    switch (field) {
      case 'name':
        return category.additionalData.name.toLowerCase();
      case 'displayOrder':
        return category.additionalData.displayOrder;
      default:
        return category.additionalData.name;
    }
  };

  const tableData = useTableData<IngredientCategory, SortField>(
    {
      data: categories,
      defaultSortField: 'displayOrder',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (categories.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay categorías de insumos</h3>
        <p className="text-gray-500">No se encontraron categorías con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Categoría
              </SortableHeader>
              <SortableHeader
                field="displayOrder"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Orden
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((category) => (
              <tr key={category.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Beaker className="h-4 w-4 text-gray-500" />
                      <div className="text-sm font-medium text-gray-900">
                        {category.additionalData.name}
                      </div>
                    </div>
                    {category.additionalData.description && (
                      <div className="text-xs text-gray-500 mt-1 ml-6">
                        {category.additionalData.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1 ml-6">
                      ID: {category.id}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge variant="outline" className="font-mono">
                    {category.additionalData.displayOrder}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge className={category.additionalData.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {category.additionalData.isActive ? 'Activa' : 'Inactiva'}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(category)}
                      className="text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(category)}
                      className={`text-xs ${category.additionalData.isActive
                        ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                    >
                      {category.additionalData.isActive ? (
                        <>
                          <PowerOff className="w-3 h-3 mr-1" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <Power className="w-3 h-3 mr-1" />
                          Reactivar
                        </>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
